import * as React from 'react';
import {useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {Link, Navigate, useNavigate, useParams} from 'react-router-dom';
import {ArrowLeft, Plus, X} from 'lucide-react';
import {OpsAssetPageQuery} from './__generated__/OpsAssetPageQuery.graphql';
import {OpsAssetPageCreateMutation} from './__generated__/OpsAssetPageCreateMutation.graphql';
import {OpsAssetPageUpdateMutation} from './__generated__/OpsAssetPageUpdateMutation.graphql';
import {OpsAssetPageDeleteMutation} from './__generated__/OpsAssetPageDeleteMutation.graphql';
import {dangerButton, ghostButton, inputClass, primaryButton} from './lib';

const QUERY = graphql`
    query OpsAssetPageQuery($id: ID!, $skip: Boolean!) {
        node(id: $id) @skip(if: $skip) {
            ... on Asset {
                id
                assetTag
                category
                make
                model
                description
                serial
                status
                rentable
                dailyRate
                rateEstimated
                replacementValue
                notes
                properties { key value }
                kit { id assetTag name }
                parentAsset { assetTag name }
                attachments { id assetTag name }
                foundReports { id email message createdAt resolvedAt }
                owner { id }
            }
        }
        entities(first: 500) { nodes { id kind name } }
    }`;

const CREATE = graphql`mutation OpsAssetPageCreateMutation($input: CreateAssetInput!) { createAsset(input: $input) { asset { id } userErrors { message } } }`;
const UPDATE = graphql`mutation OpsAssetPageUpdateMutation($input: UpdateAssetInput!) { updateAsset(input: $input) { asset { id } userErrors { message } } }`;
const DELETE = graphql`mutation OpsAssetPageDeleteMutation($input: DeleteAssetInput!) { deleteAsset(input: $input) { deletedAssetTag userErrors { message } } }`;

const CATEGORIES = [
    'CAMERA', 'LENS', 'CAMERA_ACCESSORY', 'LIGHTING', 'AUDIO_VIDEO', 'MEDIA', 'BATTERY', 'POWER', 'CABLE',
    'WORKSTATION', 'WORKSTATION_ACCESSORY', 'NETWORK', 'SECURITY', 'PRINTER', 'ETC',
] as const;
const STATUSES = ['AVAILABLE', 'IN_USE', 'MAINTENANCE', 'LOST', 'RETIRED'] as const;

type Form = {
    category: string; assetTag: string; make: string; model: string; description: string; serial: string;
    status: string; rentable: boolean; dailyRate: string; replacementValue: string; notes: string; parentAssetTag: string; kitTag: string; ownerId: string;
    properties: { key: string; value: string }[];
};

const empty: Form = {
    category: 'CAMERA', assetTag: '', make: '', model: '', description: '', serial: '', status: 'AVAILABLE',
    rentable: true, dailyRate: '', replacementValue: '', notes: '', parentAssetTag: '', kitTag: '', ownerId: '', properties: [],
};

const errorsOf = (payload: { userErrors: ReadonlyArray<{ message: string }> } | null | undefined, ok: boolean) =>
    ok ? null : payload?.userErrors.map((u) => u.message).join('; ') || 'Something went wrong';

// Create (/ops/inventory/new) or edit (/ops/inventory/:id) one piece of gear.
const OpsAssetPage: React.FC = () => {
    const {id} = useParams();
    const isNew = !id;
    const navigate = useNavigate();
    const [fetchKey, setFetchKey] = useState(0);
    const [, startTransition] = useTransition();
    const data = useLazyLoadQuery<OpsAssetPageQuery>(QUERY, {id: id ? decodeURIComponent(id) : '', skip: isNew}, {fetchKey, fetchPolicy: 'network-only'});
    const asset = isNew ? null : data.node;

    const [create, creating] = useMutation<OpsAssetPageCreateMutation>(CREATE);
    const [update, updating] = useMutation<OpsAssetPageUpdateMutation>(UPDATE);
    const [destroy, deleting] = useMutation<OpsAssetPageDeleteMutation>(DELETE);

    const [form, setForm] = useState<Form>(() => asset ? {
        category: asset.category, assetTag: asset.assetTag, make: asset.make ?? '', model: asset.model ?? '',
        description: asset.description ?? '', serial: asset.serial ?? '', status: asset.status, rentable: asset.rentable,
        // Only a staff-set rate goes in the field; a derived one is shown as a hint.
        dailyRate: asset.rateEstimated ? '' : (asset.dailyRate ?? ''), replacementValue: asset.replacementValue ?? '',
        notes: asset.notes ?? '', parentAssetTag: asset.parentAsset?.assetTag ?? '', kitTag: '', ownerId: asset.owner?.id ?? '',
        properties: asset.properties.map((p) => ({key: p.key, value: p.value})),
    } : empty);
    const [banner, setBanner] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    if (!isNew && (!asset || !asset.id)) {
        return <p className="text-gray-500">Asset not found. <Link to="/ops/inventory" className="text-lumina-purple">Back to inventory</Link></p>;
    }
    if (asset?.category === 'KIT') return <Navigate to={`/ops/kits/${encodeURIComponent(asset.id)}`} replace/>;

    const set = (patch: Partial<Form>) => { setForm((f) => ({...f, ...patch})); setSaved(false); };
    const setProp = (i: number, patch: Partial<{ key: string; value: string }>) =>
        set({properties: form.properties.map((p, j) => (j === i ? {...p, ...patch} : p))});

    const rate = form.dailyRate.trim() === '' ? 0 : Number(form.dailyRate);
    const rateValid = !Number.isNaN(rate) && rate >= 0;
    const value = form.replacementValue.trim() === '' ? 0 : Number(form.replacementValue);
    const valueValid = !Number.isNaN(value) && value >= 0;

    const common = () => ({
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        description: form.description.trim() || null,
        serial: form.serial.trim() || null,
        status: form.status,
        rentable: form.rentable,
        dailyRate: rate,
        replacementValue: value,
        notes: form.notes.trim() || null,
        properties: form.properties.filter((p) => p.key.trim() && p.value.trim()),
        parentAssetTag: form.parentAssetTag.trim() || (isNew ? null : ''),
        ownerId: form.ownerId || null,
    });

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!rateValid || !valueValid) { setBanner('Daily rate and replacement value must be numbers'); return; }
        setBanner(null);
        if (isNew) {
            create({
                variables: {input: {category: form.category as any, assetTag: form.assetTag.trim() || null, kitTag: form.kitTag.trim() || null, ...common()} as any},
                onCompleted: (res) => {
                    const error = errorsOf(res.createAsset, Boolean(res.createAsset?.asset));
                    if (error) setBanner(error); else navigate(`/ops/inventory/${encodeURIComponent(res.createAsset!.asset!.id)}`);
                },
                onError: (err) => setBanner(err.message),
            });
        } else {
            update({
                variables: {input: {assetId: asset!.id, ...common()} as any},
                onCompleted: (res) => {
                    const error = errorsOf(res.updateAsset, Boolean(res.updateAsset?.asset));
                    setBanner(error);
                    if (!error) { setSaved(true); startTransition(() => setFetchKey((k) => k + 1)); }
                },
                onError: (err) => setBanner(err.message),
            });
        }
    };

    const remove = () => destroy({
        variables: {input: {assetId: asset!.id}},
        onCompleted: (res) => {
            const error = errorsOf(res.deleteAsset, Boolean(res.deleteAsset?.deletedAssetTag));
            if (error) { setBanner(error); setConfirmingDelete(false); } else navigate('/ops/inventory');
        },
        onError: (err) => setBanner(err.message),
    });

    const busy = creating || updating || deleting;
    const field = (label: string, node: React.ReactNode, hint?: React.ReactNode) => (
        <label className="block text-xs uppercase tracking-wider text-gray-500">
            {label}
            {node}
            {hint && <span className="block normal-case tracking-normal text-[11px] text-gray-600 mt-1">{hint}</span>}
        </label>
    );
    const text = (key: keyof Form, placeholder = '') => (
        <input value={form[key] as string} onChange={(e) => set({[key]: e.target.value} as any)} placeholder={placeholder}
               className={`${inputClass} block mt-1 w-full text-white`}/>
    );

    return (
        <div className="max-w-3xl">
            <Link to="/ops/inventory" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white mb-4">
                <ArrowLeft className="w-3 h-3"/> Inventory
            </Link>
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-display font-bold">{isNew ? 'New item' : asset!.assetTag}</h1>
                    {!isNew && (
                        <p className="text-gray-500 text-sm mt-1">
                            {asset!.category.replace(/_/g, ' ')}
                            {asset!.kit && <> · in kit <Link to={`/ops/kits/${encodeURIComponent(asset!.kit.id)}`} className="text-lumina-accent">{asset!.kit.assetTag}</Link> {asset!.kit.name}</>}
                        </p>
                    )}
                </div>
                {!isNew && (
                    confirmingDelete ? (
                        <div className="flex gap-2">
                            <button onClick={remove} disabled={busy} className={dangerButton}>Yes, delete {asset!.assetTag}</button>
                            <button onClick={() => setConfirmingDelete(false)} className={ghostButton}>Keep it</button>
                        </div>
                    ) : (
                        <button onClick={() => setConfirmingDelete(true)} className={dangerButton}>Delete</button>
                    )
                )}
            </div>

            {banner && (
                <div className="mb-4 px-4 py-2 border border-red-500/40 bg-red-950/30 text-red-300 text-sm flex justify-between">
                    <span>{banner}</span><button onClick={() => setBanner(null)}><X className="w-4 h-4"/></button>
                </div>
            )}

            <form onSubmit={submit} className="space-y-5">
                {isNew && (
                    <div className="grid md:grid-cols-2 gap-4">
                        {field('Category', (
                            <select value={form.category} onChange={(e) => set({category: e.target.value})} className={`${inputClass} block mt-1 w-full`}>
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                            </select>
                        ), 'Fixed once created; the tag encodes it. Kits are created from "New kit".')}
                        {field('Asset tag', text('assetTag', 'Leave blank to assign the next tag'), 'Only if the item is already labelled.')}
                    </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                    {field('Make', text('make', 'BlackMagic'))}
                    {field('Model', text('model', 'Pocket Cinema Camera 6K Pro'))}
                </div>
                {field('Description', text('description', 'What it is, if make/model don\'t say'))}
                <div className="grid md:grid-cols-4 gap-4">
                    {field('Serial', text('serial'))}
                    {field('Status', (
                        <select value={form.status} onChange={(e) => set({status: e.target.value})} className={`${inputClass} block mt-1 w-full`}>
                            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                        </select>
                    ))}
                    {field('Daily rate ($)', text('dailyRate', asset?.rateEstimated && asset.dailyRate ? `≈ ${asset.dailyRate} derived` : 'blank = derive'),
                        'Blank uses 1/20 of the replacement value.')}
                    {field('Replacement value ($)', text('replacementValue', 'MSRP'), 'What it would cost to replace.')}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    {field('Belongs to device', text('parentAssetTag', 'LWM-CAM-000004'), 'For power supplies and accessories. Blank detaches.')}
                    {isNew && field('Add to kit', text('kitTag', 'LWM-KIT-000002'))}
                    {field('Owner', (
                        <select value={form.ownerId} onChange={(e) => set({ownerId: e.target.value})} className={`${inputClass} block mt-1 w-full`}>
                            <option value="">No owner set</option>
                            {data.entities.nodes.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.kind.toLowerCase()})</option>)}
                        </select>
                    ), <>Person or company that owns it. <Link to="/ops/owners" className="text-lumina-purple">Manage owners</Link></>)}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input type="checkbox" checked={form.rentable} onChange={(e) => set({rentable: e.target.checked})}/>
                    Listed for rent on the public site
                </label>

                <div>
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs uppercase tracking-wider text-gray-500">Properties</span>
                        <button type="button" onClick={() => set({properties: [...form.properties, {key: '', value: ''}]})}
                                className="text-xs text-gray-400 hover:text-white flex items-center gap-1"><Plus className="w-3 h-3"/> add</button>
                    </div>
                    {form.properties.length === 0 && <p className="text-xs text-gray-600">mount, aperture, capacity, interface, mah… shown as spec chips on the public page.</p>}
                    <div className="space-y-2">
                        {form.properties.map((p, i) => (
                            <div key={i} className="flex gap-2">
                                <input value={p.key} onChange={(e) => setProp(i, {key: e.target.value})} placeholder="key" className={`${inputClass} w-40 text-white`}/>
                                <input value={p.value} onChange={(e) => setProp(i, {value: e.target.value})} placeholder="value" className={`${inputClass} flex-1 text-white`}/>
                                <button type="button" onClick={() => set({properties: form.properties.filter((_, j) => j !== i)})} className="text-gray-500 hover:text-red-400"><X className="w-4 h-4"/></button>
                            </div>
                        ))}
                    </div>
                </div>

                {field('Notes', (
                    <textarea value={form.notes} onChange={(e) => set({notes: e.target.value})} rows={3} className={`${inputClass} block mt-1 w-full text-white`}/>
                ), 'Staff only; never shown to customers.')}

                <div className="flex items-center gap-3">
                    <button type="submit" disabled={busy} className={primaryButton}>{isNew ? 'Create item' : 'Save'}</button>
                    {saved && <span className="text-xs text-emerald-400">Saved</span>}
                </div>
            </form>

            {!isNew && asset!.foundReports.length > 0 && (
                <section className="mt-8">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Found reports · from the public lost-asset page</h2>
                    <ul className="border border-amber-400/30 divide-y divide-gray-900 text-sm">
                        {asset!.foundReports.map((r) => (
                            <li key={r.id} className={`px-3 py-2 ${r.resolvedAt ? 'opacity-50' : ''}`}>
                                <div className="flex gap-4">
                                    <a href={`mailto:${r.email}`} className="text-lumina-accent">{r.email}</a>
                                    <span className="text-gray-500 text-xs">{new Date(r.createdAt).toLocaleString()}</span>
                                    {r.resolvedAt && <span className="text-gray-500 text-xs">resolved</span>}
                                </div>
                                {r.message && <p className="text-gray-300 text-sm mt-1 whitespace-pre-line">{r.message}</p>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {!isNew && asset!.attachments.length > 0 && (
                <section className="mt-8">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Attached to this</h2>
                    <ul className="border border-gray-800 divide-y divide-gray-900 text-sm">
                        {asset!.attachments.map((a) => (
                            <li key={a.id} className="px-3 py-2 flex gap-4">
                                <Link to={`/ops/inventory/${encodeURIComponent(a.id)}`} className="font-mono text-xs text-gray-400 hover:text-white">{a.assetTag}</Link>
                                <span className="text-white">{a.name}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

export default OpsAssetPage;
